import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { listArtifactTemplates, upsertArtifactTemplate, deleteArtifactTemplate, seedArtifactTemplate,
  previewArtifactTemplate } from './api';

const SCOPE_LABELS = {
  default: 'Default',
  script: 'Script',
  av_script: 'A/V Script (3-column)',
  quiz: 'Quiz',
  attachment: 'Handout / Attachment',
  exam: 'Exam',
};
const PAGE_SIZES = [['letter', 'Letter'], ['a4', 'A4'], ['legal', 'Legal']];
const KEY = ['training-artifact-templates'];

function TemplateCard({ tpl, canEdit }) {
  const qc = useQueryClient();
  const isDefault = tpl.scope === 'default';
  const [form, setForm] = useState({
    header_text: tpl.header_text || '',
    footer_text: tpl.footer_text || '',
    page_size: tpl.page_size || 'letter',
    page_numbers: tpl.page_numbers,
    active: tpl.active,
  });
  const docxRef = useRef(null);
  const logoRef = useRef(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const save = useMutation({
    mutationFn: (extra = {}) => {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (docxRef.current?.files?.[0]) fd.append('template_file', docxRef.current.files[0]);
      if (logoRef.current?.files?.[0]) fd.append('logo', logoRef.current.files[0]);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      return upsertArtifactTemplate(tpl.scope, fd);
    },
    onSuccess: () => {
      toast.success('Template saved');
      if (docxRef.current) docxRef.current.value = '';
      if (logoRef.current) logoRef.current.value = '';
      invalidate();
    },
    onError: (e) => toast.error(e?.message || 'Save failed'),
  });
  const seed = useMutation({
    mutationFn: () => seedArtifactTemplate(tpl.scope),
    onSuccess: (r) => { toast.success(`Base pulled from ${r?.sourced_from || 'ZU class doc'}`); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Seed failed'),
  });
  // Downloads a sample document rendered with this template. Answers "how do I see
  // what this looks like" without producing a throwaway class and downloading its
  // script, which was the only way before.
  const preview = useMutation({
    mutationFn: () => previewArtifactTemplate(tpl.scope),
    onError: (e) => toast.error(e?.message || 'Could not render the preview'),
  });
  const removeOverride = useMutation({
    mutationFn: () => deleteArtifactTemplate(tpl.scope),
    onSuccess: () => { toast.success('Override removed'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = 'w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800 disabled:opacity-50';

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{SCOPE_LABELS[tpl.scope] || tpl.scope}</h3>
          {isDefault
            ? <span className="text-[10px] bg-blue-500/15 text-blue-500 px-2 py-0.5 rounded-full">applies to all kinds</span>
            : !tpl.exists && <span className="text-[10px] text-gray-400">uses Default until set</span>}
        </div>
        {!isDefault && tpl.exists && canEdit && (
          <button onClick={() => removeOverride.mutate()} className="text-xs text-red-500">remove override</button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-gray-500">Header text
          <input className={field} disabled={!canEdit} value={form.header_text}
            onChange={(e) => set('header_text', e.target.value)} placeholder="e.g. Zeffert University" />
        </label>
        <label className="text-xs text-gray-500">Footer text
          <input className={field} disabled={!canEdit} value={form.footer_text}
            onChange={(e) => set('footer_text', e.target.value)} placeholder="e.g. Confidential" />
        </label>
        <label className="text-xs text-gray-500">Page size
          <select className={field} disabled={!canEdit} value={form.page_size}
            onChange={(e) => set('page_size', e.target.value)}>
            {PAGE_SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <div className="flex items-end gap-4 pb-1">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input type="checkbox" disabled={!canEdit} checked={!!form.page_numbers}
              onChange={(e) => set('page_numbers', e.target.checked)} /> Page numbers
          </label>
          {!isDefault && (
            <label className="flex items-center gap-2 text-xs text-gray-500">
              <input type="checkbox" disabled={!canEdit} checked={!!form.active}
                onChange={(e) => set('active', e.target.checked)} /> Active
            </label>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="text-xs text-gray-500">
          <div className="mb-1">Word base (.docx) - header/footer/styles</div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] ${tpl.has_template_file ? 'text-blue-500' : 'text-gray-400'}`}>
              {tpl.has_template_file ? (tpl.template_file_name || 'attached') : 'none'}
            </span>
            {tpl.has_template_file && canEdit && (
              <button onClick={() => save.mutate({ remove_template_file: true })} className="text-[11px] text-red-500">remove</button>
            )}
          </div>
          <input ref={docxRef} type="file" accept=".docx" disabled={!canEdit} className="mt-1 text-[11px]" />
          {canEdit && (
            <button onClick={() => seed.mutate()} disabled={seed.isPending}
              className="mt-2 block text-[11px] text-blue-500 disabled:opacity-40">
              {seed.isPending ? 'Pulling…' : 'Pull base from a ZU class doc'}
            </button>
          )}
        </div>
        <div className="text-xs text-gray-500">
          <div className="mb-1">Header logo (image)</div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] ${tpl.has_logo ? 'text-blue-500' : 'text-gray-400'}`}>
              {tpl.has_logo ? 'attached' : 'none'}
            </span>
            {tpl.has_logo && canEdit && (
              <button onClick={() => save.mutate({ remove_logo: true })} className="text-[11px] text-red-500">remove</button>
            )}
          </div>
          <input ref={logoRef} type="file" accept="image/*" disabled={!canEdit} className="mt-1 text-[11px]" />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        {canEdit && (
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="text-sm font-bold px-4 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40">
            {save.isPending ? 'Saving…' : 'Save template'}
          </button>
        )}
        <button onClick={() => preview.mutate()} disabled={preview.isPending}
          className="text-sm px-4 py-2 rounded-lg border border-blue-500 text-blue-500 disabled:opacity-40">
          {preview.isPending ? 'Rendering…' : 'Preview a sample document'}
        </button>
        <span className="text-[11px] text-gray-400">Renders the saved template, so save first.</span>
      </div>
    </div>
  );
}

export default function DocTemplates() {
  const { data, isLoading } = useQuery({ queryKey: KEY, queryFn: listArtifactTemplates });
  const canEdit = !!data?.can_edit;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h2 className="text-lg font-semibold">Document Templates</h2>
        <span className="text-[10px] bg-gray-900 text-orange-400 px-2 py-0.5 rounded-full">Director / Admin only</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Branding for produced Word docs. The <strong>Default</strong> applies to every artifact; add a per-kind
        template to override it. A Word base carries the header, footer, and styles; the fields below fill in or
        override text. Pull a base from a ZU class doc to match your existing look, then adjust anytime.
      </p>
      {isLoading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        (data?.templates || []).map((t) => <TemplateCard key={t.scope} tpl={t} canEdit={canEdit} />)
      )}
    </div>
  );
}
